import './as-header-set.ts';
import '../../base/buttons/as-icon-button';
import '../../overlays/as-tooltip-wrapper.ts';
import styleText from './as-state-set.css?raw';

/**
 * State management section component
 */
export class TrStateSet extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      TrStateSet.sheet
    ];

    this.shadowRoot!.innerHTML = /*html*/`
      <div set-id="header-state-set">
        <as-tooltip-wrapper show-on-hover placement="bottom" id="undo-tt">
          <as-icon-button
            file-name="header/undo.png" black disabled
            name="undo" disable-tooltips
          ></as-icon-button>
          <as-tooltip name="default">Undo</as-tooltip>
          <as-tooltip name="warn">
            <div style="display:flex;align-items:center;gap:0.375rem;white-space:nowrap;">
              <span>Undoing will clear redo stack!</span>
              <as-icon-button file-name="icons/check-mark.svg" black disable-tooltips data-confirm></as-icon-button>
              <as-icon-button file-name="icons/close.svg" black disable-tooltips data-dismiss></as-icon-button>
            </div>
          </as-tooltip>
        </as-tooltip-wrapper>
        <as-icon-button
          file-name="header/redo.png" black disabled
          tooltip-dict-key="tooltip.state.redo"
          tooltip-default-text="Redo"
          name="redo"
        ></as-icon-button>
      </div>
    `;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    const sr = this.shadowRoot!;

    sr.querySelector('as-icon-button[name="undo"]')?.addEventListener('as-button-click', () => {
      if (document.querySelector('as-app')?.hasAttribute('disable-undo-history')) {
        this.dispatchEvent(new CustomEvent('undo', { bubbles: true, composed: true }));
      } else {
        window.dispatchEvent(new CustomEvent('as-undo'));
      }
    });

    sr.querySelector('as-icon-button[name="redo"]')?.addEventListener('as-button-click', () => {
      if (document.querySelector('as-app')?.hasAttribute('disable-undo-history')) {
        this.dispatchEvent(new CustomEvent('redo', { bubbles: true, composed: true }));
      } else {
        window.dispatchEvent(new CustomEvent('as-redo'));
      }
    });
  }

  connectedCallback() {
    window.addEventListener('as-history-changed', this._handleHistoryChanged);
    window.addEventListener('as-undo-requires-confirm', this._handleUndoRequiresConfirm);
    this.addEventListener('as-tooltip-confirm', this._handleTooltipConfirm);

    if (document.querySelector('as-app')?.hasAttribute('disable-undo-history')) {
      const sr = this.shadowRoot!;
      sr.querySelector('as-icon-button[name="undo"]')?.removeAttribute('disabled');
      sr.querySelector('as-icon-button[name="redo"]')?.removeAttribute('disabled');
    }
  }

  disconnectedCallback() {
    window.removeEventListener('as-history-changed', this._handleHistoryChanged);
    window.removeEventListener('as-undo-requires-confirm', this._handleUndoRequiresConfirm);
    this.removeEventListener('as-tooltip-confirm', this._handleTooltipConfirm);
  }

  private _handleUndoRequiresConfirm = () => {
    (this.shadowRoot!.querySelector('#undo-tt') as any)?.showNamedTooltip('warn');
  };

  private _handleTooltipConfirm = () => {
    window.dispatchEvent(new CustomEvent('as-undo-confirmed'));
  };

  private _handleHistoryChanged = (e: Event) => {
    // When the host app manages its own undo/redo (disable-undo-history), the
    // built-in history stack is disabled and always reports canUndo/canRedo=false.
    // Leave the buttons enabled (force-enabled in connectedCallback) under the
    // host app's control instead of disabling them from the empty internal stack.
    if (document.querySelector('as-app')?.hasAttribute('disable-undo-history')) return;
    const { canUndo, canRedo } = (e as CustomEvent).detail ?? {};
    const sr = this.shadowRoot!;
    sr.querySelector('as-icon-button[name="undo"]')?.toggleAttribute('disabled', !canUndo);
    sr.querySelector('as-icon-button[name="redo"]')?.toggleAttribute('disabled', !canRedo);
  };
}

customElements.define('as-state-set', TrStateSet); 
